if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        if (!this.form) return;
        const idInput = this.form?.querySelector('[name=id]');
        if (idInput) idInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        // NOTE: our theme sometimes renders the primary button as `type="button"` (BIS modal)
        // and later flips it to `type="submit"` (bundle remaps). Don't assume a submit-type button exists.
        this.submitButton = this.querySelector('[type="submit"]') || this.querySelector('button.product-form__submit');

        if (document.querySelector('cart-drawer') && this.submitButton) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        const submitter = evt.submitter && evt.submitter.tagName ? evt.submitter : this.submitButton;
        if (!submitter) return;
        this.submitButton = submitter;

        // Mapped bundle cards can be re-rendered by variant/inventory scripts after
        // Liquid has selected the real sellable variant. Reapply that variant at
        // submit time so the cart request always uses the mapped bundle SKU.
        const variantInput = this.form.querySelector('[name=id]');
        const forcedVariantId = String(variantInput?.dataset.forceMappedVariantId || '').trim();
        if (/^\d+$/.test(forcedVariantId)) {
          variantInput.value = forcedVariantId;
          variantInput.setAttribute('value', forcedVariantId);
          variantInput.disabled = false;
        }

        const buttonType = this.submitButton.getAttribute('type');
        if (buttonType && buttonType !== 'submit') return;
        if (this.submitButton.getAttribute('aria-disabled') === 'true' || this.submitButton.dataset.egAtcState === 'pending') return;
        this.error = false;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.setAttribute('aria-busy', 'true');
        this.submitButton.classList.add('loading');
        window.EGAtcFeedback?.pending(this.submitButton);
        const loadingSpinner = this.querySelector('.loading__spinner, .loading-overlay__spinner');
        if (loadingSpinner) loadingSpinner.classList.remove('hidden');
        this.syncStickyLoading(true);

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        // Check if this is a virtual bundle
        const variantId = variantInput ? String(variantInput.value || '').trim() : '';
        if (!/^\d+$/.test(variantId)) {
          this.handleErrorMessage('Please select an available option before adding to cart.');
          this.error = true;
          this.submitButton.classList.remove('loading');
          window.EGAtcFeedback?.idle(this.submitButton);
          this.submitButton.removeAttribute('aria-disabled');
          this.submitButton.removeAttribute('aria-busy');
          if (loadingSpinner) loadingSpinner.classList.add('hidden');
          this.syncStickyLoading(false);
          return;
        }
        const preflightProperties = {};
        new FormData(this.form).forEach((value, key) => {
          const propertyMatch = key.match(/^properties\[(.+)\]$/);
          if (propertyMatch) preflightProperties[propertyMatch[1]] = value;
        });
        try {
          await window.EGRegiosBedGift?.prepareVariant?.(variantId, {
            productHandle: this.dataset.productHandle,
            productId: this.dataset.productId,
            properties: preflightProperties,
          });
        } catch (error) {
          this.handleErrorMessage('Unable to prepare this item. Please try again.');
          this.submitButton.classList.remove('loading');
          this.submitButton.removeAttribute('aria-disabled');
          window.EGAtcFeedback?.idle(this.submitButton);
          if (loadingSpinner) loadingSpinner.classList.add('hidden');
          this.syncStickyLoading(false);
          return;
        }
        const virtualBundleRequested = variantInput && variantInput.dataset.isVirtualBundle === 'true';
        const isVirtualBundle = !!virtualBundleRequested;
        const bundleQuantity = isVirtualBundle ? (parseInt(variantInput.dataset.bundleQuantity) || 4) : null;

        if (isVirtualBundle) {
          // Virtual bundle: use JSON body and multiply quantity. Preserve all
          // line-item properties injected into the form, including Purple Dot's
          // release metadata.
          // console.log(`Virtual Bundle Quick Add: Adding ${bundleQuantity}x of variant ${variantId}`);
          
          // Set active element for cart drawer (same as regular products)
          if (this.cart) {
            this.cart.setActiveElement(document.activeElement);
          }
          
          const virtualBundleFormData = new FormData(this.form);
          const virtualBundleProperties = {};
          virtualBundleFormData.forEach((value, key) => {
            const propertyMatch = key.match(/^properties\[(.+)\]$/);
            if (propertyMatch) virtualBundleProperties[propertyMatch[1]] = value;
          });
          const virtualBundleItem = {
            id: Number(variantId),
            quantity: bundleQuantity
          };
          if (Object.keys(virtualBundleProperties).length) {
            virtualBundleItem.properties = virtualBundleProperties;
          }

          const virtualBundleItems = window.EGRegiosBedGift?.withGift([virtualBundleItem]) || [virtualBundleItem];

          fetch(`${routes.cart_add_url}.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
                body: JSON.stringify({ items: virtualBundleItems })
          })
            .then((response) => {
              // console.log('Virtual Bundle: cart/add response status:', response.status);
              // console.log('Virtual Bundle: cart/add content-type:', response.headers.get('content-type'));
              if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
              }
              return response.json();
            })
            .then(async (response) => {
              // console.log('Virtual Bundle: cart/add parsed response:', response);
              
              if (response.status && response.status !== 200) {
                publish(PUB_SUB_EVENTS.cartError, {
                  source: 'product-form',
                  productVariantId: variantId,
                  errors: response.errors || response.description,
                  message: response.message,
                });
                this.handleErrorMessage(response.description);

                const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
                if (!soldOutMessage) return;
                this.submitButton.setAttribute('aria-disabled', true);
                this.submitButton.querySelector('span').classList.add('hidden');
                soldOutMessage.classList.remove('hidden');
                this.error = true;
                return;
              }

              if (!this.cart) {
                // console.log('Virtual Bundle: No cart element found');
                return;
              }

              // Fetch sections individually (same approach as cart drawer's renderContents expects)
              const sectionsToRender = this.cart.getSectionsToRender();
              // console.log('Virtual Bundle: Fetching sections:', sectionsToRender.map(s => s.id));
              
              const sectionsData = {};
              await Promise.all(
                sectionsToRender.map(async (section) => {
                  const sectionResponse = await fetch(`${routes.cart_url}?section_id=${section.id}`);
                  sectionsData[section.id] = await sectionResponse.text();
                })
              );
              
              // console.log('Virtual Bundle: Sections fetched:', Object.keys(sectionsData));

              // Build response object for renderContents (same structure as regular add-to-cart)
              const parsedState = {
                items: response.items || [response],
                sections: sectionsData
              };

              if (!this.error)
                publish(PUB_SUB_EVENTS.cartUpdate, {
                  source: 'product-form',
                  productVariantId: variantId,
                  cartData: parsedState,
                });
              this.error = false;
              this.playAddedState();

              const quickAddModal = this.closest('quick-add-modal');
              if (quickAddModal) {
                document.body.addEventListener(
                  'modalClosed',
                  () => {
                    setTimeout(() => {
                      this.renderCartUpdate(parsedState, variantId);
                    });
                  },
                  { once: true }
                );
                quickAddModal.hide(true);
              } else {
                // console.log('Virtual Bundle: Calling renderContents');
                this.renderCartUpdate(parsedState, variantId);
              }
            })
            .catch((e) => {
              console.error('Virtual Bundle Error:', e);
              this.handleErrorMessage(e?.description || e?.message || 'Unable to add this item. Please try again.');
            })
            .finally(() => {
              this.submitButton.classList.remove('loading');
              window.EGAtcFeedback?.idle(this.submitButton);
              this.submitButton.removeAttribute('aria-busy');
              if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
              if (!this.error) this.submitButton.removeAttribute('aria-disabled');
              if (loadingSpinner) loadingSpinner.classList.add('hidden');
              this.syncStickyLoading(false);
            });
        } else {
          // Regular product: use the standard FormData approach.
          const formData = new FormData(this.form);
          // Purple Dot and mapped bundle controls can clear the hidden input
          // after submit. Preserve the validated ID captured before the async
          // gift availability check.
          formData.set('id', variantId);
          const properties = {};
          formData.forEach((value, key) => {
            const match = key.match(/^properties\[(.+)\]$/);
            if (match && value) properties[match[1]] = value;
          });
          if (this.cart) {
            formData.append(
              'sections',
              this.cart.getSectionsToRender().map((section) => section.id)
            );
            formData.append('sections_url', window.location.pathname);
            this.cart.setActiveElement(document.activeElement);
          }
          const isRegiosQualifier = window.EGRegiosBedGift?.shouldAtomicAddVariant(
            formData.get('id'),
            {
              productId: this.dataset.productId,
              properties,
            }
          );
          if (isRegiosQualifier) {
            const quantity = Number(formData.get('quantity') || 1);
            const item = {
              id: Number(formData.get('id')),
              quantity: Number.isInteger(quantity) && quantity > 0 ? quantity : 1,
              ...(Object.keys(properties).length ? { properties } : {}),
            };
            const sellingPlan = formData.get('selling_plan');
            if (sellingPlan) item.selling_plan = Number(sellingPlan);
            config.headers['Content-Type'] = 'application/json';
            config.body = JSON.stringify({
              items: window.EGRegiosBedGift.withGift([item]),
              ...(this.cart ? {
                sections: this.cart.getSectionsToRender().map((section) => section.id),
                sections_url: window.location.pathname,
              } : {}),
            });
          } else {
            config.body = formData;
          }

          fetch(`${routes.cart_add_url}`, config)
            .then((response) => {
              if (!response.ok) {
                return response.json().then((err) => { throw err; });
              }
              return response.json();
            })
            .then((response) => {
              if (response.status) {
                publish(PUB_SUB_EVENTS.cartError, {
                  source: 'product-form',
                  productVariantId: formData.get('id'),
                  errors: response.errors || response.description,
                  message: response.message,
                });
                this.handleErrorMessage(response.description);

                const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
                if (!soldOutMessage) return;
                this.submitButton.setAttribute('aria-disabled', true);
                this.submitButton.querySelector('span').classList.add('hidden');
                soldOutMessage.classList.remove('hidden');
                this.error = true;
                return;
              } else if (!this.cart) {
                return;
              }

              if (!this.error)
                publish(PUB_SUB_EVENTS.cartUpdate, {
                  source: 'product-form',
                  productVariantId: formData.get('id'),
                  cartData: response,
                });
              this.error = false;
              this.playAddedState();
              const quickAddModal = this.closest('quick-add-modal');
              if (quickAddModal) {
                document.body.addEventListener(
                  'modalClosed',
                  () => {
                    setTimeout(() => {
                      this.renderCartUpdate(response, formData.get('id'));
                    });
                  },
                  { once: true }
                );
                quickAddModal.hide(true);
              } else {
                this.renderCartUpdate(response, formData.get('id'));
              }
            })
            .catch((e) => {
              console.error(e);
              this.handleErrorMessage(e?.description || e?.message || 'Unable to add this item. Please try again.');
            })
            .finally(() => {
              this.submitButton.classList.remove('loading');
              window.EGAtcFeedback?.idle(this.submitButton);
              this.submitButton.removeAttribute('aria-busy');
              if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
              if (!this.error) this.submitButton.removeAttribute('aria-disabled');
              if (loadingSpinner) loadingSpinner.classList.add('hidden');
              this.syncStickyLoading(false);
            });
        }
      }

      renderCartUpdate(cartData, triggerId) {
        const canUpdateOpenDrawerInPlace =
          this.cart?.tagName === 'CART-DRAWER' &&
          this.cart.classList.contains('active') &&
          cartData?.sections &&
          typeof window.refreshCartDrawerHotZones === 'function';

        if (canUpdateOpenDrawerInPlace && window.refreshCartDrawerHotZones(cartData, triggerId)) {
          return;
        }

        this.cart?.renderContents(cartData);
      }

      syncStickyLoading(isLoading) {
        document.querySelectorAll('.pdp-sticky-selector .sticky-atc:not(.sticky-atc--early-access)').forEach((button) => {
          button.disabled = isLoading;
          button.classList.toggle('loading', isLoading);
          button.setAttribute('aria-busy', String(isLoading));
          window.EGAtcFeedback?.[isLoading ? 'pending' : 'idle'](button);
          button.querySelector('.loading__spinner, .loading-overlay__spinner')?.classList.toggle('hidden', !isLoading);
        });
      }

      playAddedState() {
        if (!this.submitButton || this.error) return;
        window.EGAtcFeedback?.success(this.submitButton);
        if (this.closest('.product')) {
          document.querySelectorAll('.pdp-sticky-selector .sticky-atc:not(.sticky-atc--early-access)').forEach(button => window.EGAtcFeedback?.success(button));
        }
      }

      handleErrorMessage(errorMessage = false) {
        window.EGAtcFeedback?.clearError(this.submitButton);

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper || this.hideErrors) {
          if (errorMessage) window.EGAtcFeedback?.error(this.submitButton, errorMessage);
          return;
        }
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    }
  );
}
